import type { SharedTypes } from '@semios/app-platform-banyan-route-definitions'
import type { TEmitterType } from '@semios/app-platform-values-helpers-and-types'
import { IRRIGATION_SCHEDULER_FIRST_DAY_OF_WEEK } from '@semios/app-platform-values-helpers-and-types'
import type { TIrrigationDeviceStatus } from 'App/Map/types'
import type { TFertigationSettingsData } from 'App/Map/UserSettingsMenu/Shared/FertigationConfiguration/types'
import type { Moment } from 'moment-timezone'
import moment from 'moment-timezone'
import { PersistentStoreName } from 'stores/_utils/persistentStore/persistenceStoresCleanUpMap'
import { persistentInit } from 'stores/_utils/persistentStore/persistentStore'
import type {
  TValidIrrigationSchedulerZoneEmitter,
  TValidIrrigationSchedulerZoneEmitterId,
} from 'stores/fieldAssetStore/helperTypes'
import type { TZoneWithETDataArray } from './types'

export enum TScheduledEventEnum {
  DRAFT_CREATE = 'DRAFT_CREATE',
  DRAFT_DELETE = 'DRAFT_DELETE',
  SCHEDULED = 'SCHEDULED',
  STOPPED = 'STOPPED',
  PENDING_DELETE = 'PENDING_DELETE',
}

export type EVENT_ID = string

export type ISOString = string

export type DATE_RANGE = { dateFrom: Moment; dateTo: Moment }

export type EVENT = DATE_RANGE & {
  emitterType: TEmitterType
  irrigationZoneId: string
  irrigationZoneEmitterId: TValidIrrigationSchedulerZoneEmitterId
  externalEventId?: string
}

export type CONTINUOUS_IRRIGATION_EVENT = EVENT & {
  eventId: EVENT_ID
  fertigationSchedules: (TFertigationEvent & {
    fertilizerName: string
    injectorName: string
    flowRate?: number | null
    flowRateUnit?: SharedTypes.IrrigationScheduler.TFertigationFlowRateUnit | null
  })[]
}

type TFertigationEvent = {
  fertigationStartDelay: number
  duration: number
  fertilizerId: number
  injectorId: number
  fertigationStartTime: Moment
  fertigationEndTime: Moment
}

type TAppliedFertigationEvents = {
  endTime: Moment
  fertigationType: string
  fertilizerName: string
  injectorName: string
  startTime: Moment
  unit: string
  volume: number
}

export type PULSE_IRRIGATION_EVENT = EVENT & {
  pulseEventId: EVENT_ID
  pulseIrrigationConfig: {
    onDuration: number
    offDuration: number
    numberOfCycles: number
  }
  pulseIrrigationEvents: {
    dateFrom: Moment
    dateTo: Moment
    eventId: string
    externalEventId?: string
    status: TScheduledEventEnum
  }[]
}

export type PUBLISHED_EVENT = EVENT & {
  eventId: EVENT_ID
  status: string
  appliedFertigationEvents: TAppliedFertigationEvents[]
}

// Applied events currently have no concept of pulse/continuous
export type APPLIED_EVENT = EVENT & {
  status: string
  appliedFertigationEvents: TAppliedFertigationEvents[]
}

export type SCHEDULED_PULSE_EVENT = PULSE_IRRIGATION_EVENT & {
  type: TScheduledEventEnum
}

export type SCHEDULED_CONTINUOUS_EVENT = CONTINUOUS_IRRIGATION_EVENT & {
  type: TScheduledEventEnum
}

export enum TDateViewEnum {
  DAY = 'DAY',
  WEEK = 'WEEK',
  TWO_WEEKS = 'TWO_WEEKS',
}

export const DAYS_BY_DATE_VIEW: Record<TDateViewEnum, number> = {
  [TDateViewEnum.TWO_WEEKS]: 14,
  [TDateViewEnum.WEEK]: 7,
  [TDateViewEnum.DAY]: 1,
}

export const NO_3RD_PARTY_IRRIGATION_VENDOR = 'NO_3RD_PARTY_IRRIGATION_VENDOR'

export type TIrrigationEventType = 'CONTINUOUS' | 'PULSE'

export type TFormFertigationEvent = {
  fertigationStartDelayHours?: number
  fertigationStartDelayMinutes?: number
  fertigationDurationHours?: number
  fertigationDurationMinutes?: number
  fertilizerId?: number
  injectorId?: number
}

export type FormValuesCreate = {
  irrigationEventType: TIrrigationEventType
  irrigationZoneEmitterIds: TValidIrrigationSchedulerZoneEmitterId[]
  startDate: Date | undefined
  durationHoursContinuous?: number
  durationMinutesContinuous?: number
  durationHoursPulseOn?: number
  durationMinutesPulseOn?: number
  durationHoursPulseOff?: number
  durationMinutesPulseOff?: number
  numberOfPulseIrrigationCycles?: number
  isRepeatSchedule: boolean
  repeatDays: number[] // array of day indexes
  repeatEndDate: Date | undefined
  fertigationEvents: Record<string, TFormFertigationEvent>
}

export type FormValuesEdit = {
  irrigationEventType: TIrrigationEventType
  irrigationZoneEmitterId: TValidIrrigationSchedulerZoneEmitterId
  startDate: Date
  durationHoursContinuous?: number
  durationMinutesContinuous?: number
  durationHoursPulseOn?: number
  durationMinutesPulseOn?: number
  durationHoursPulseOff?: number
  durationMinutesPulseOff?: number
  numberOfPulseIrrigationCycles?: number
  fertigationEvents: Record<string, TFormFertigationEvent>
}

export type TIrrigationZoneEmitterWithCurrentEmitterStatus = TValidIrrigationSchedulerZoneEmitter & {
  currentEmitterStatus: TIrrigationDeviceStatus
}

export type TIrrigationSchedulerStoreState = {
  batchDelete: {
    visible: boolean
    events: {
      [eventId: string]:
        | (Omit<SCHEDULED_CONTINUOUS_EVENT, 'type'> & {
            type: TScheduledEventEnum.SCHEDULED | TScheduledEventEnum.DRAFT_CREATE
          })
        | (Omit<SCHEDULED_PULSE_EVENT, 'type'> & {
            type: TScheduledEventEnum.SCHEDULED | TScheduledEventEnum.DRAFT_CREATE
          })
    }
  }
  showPublishScheduleConfirmation: boolean
  showLauncher: boolean
  visible: boolean
  dateView: TDateViewEnum
  dateFrom: ISOString
  showPeakHours: boolean
  selectedIrrigationZoneEmitterIds: TValidIrrigationSchedulerZoneEmitterId[]
  appliedEventsByIrrigationZoneEmitterId: {
    [irrigationZoneEmitterId: TValidIrrigationSchedulerZoneEmitterId]: APPLIED_EVENT[]
  }
  externalPublishedEventsByIrrigationZoneEmitterId: {
    [irrigationZoneEmitterId: TValidIrrigationSchedulerZoneEmitterId]: PUBLISHED_EVENT[]
  }
  externalPublishedPulseIrrigationEventsByIrrigationZoneEmitterId: {
    [irrigationZoneEmitterId: TValidIrrigationSchedulerZoneEmitterId]: PUBLISHED_EVENT[]
  }
  fertigationSettings: TFertigationSettingsData[]
  // TODO: Data is denormalized (stored in both flat and grouped structures).
  // This creates risk of sync bugs if both aren't updated atomically.
  // We chose this tradeoff because:
  // 1. Chart rendering (hot path) needs O(1) access by emitter ID
  // 2. Event panels/editing (cold path) should have O(1) access by event ID
  // All actions that modify events MUST update both structures atomically.
  scheduledEvents: Record<EVENT_ID, SCHEDULED_CONTINUOUS_EVENT>
  scheduledEventsByIrrigationZoneEmitterId: {
    [irrigationZoneEmitterId: TValidIrrigationSchedulerZoneEmitterId]: SCHEDULED_CONTINUOUS_EVENT[]
  }
  scheduledPulseIrrigationEvents: Record<EVENT_ID, SCHEDULED_PULSE_EVENT>
  scheduledPulseEventsByIrrigationZoneEmitterId: {
    [irrigationZoneEmitterId: TValidIrrigationSchedulerZoneEmitterId]: SCHEDULED_PULSE_EVENT[]
  }
  stoppedIrrigationZoneEmitters: Record<string, ISOString>
  eventCreate: null | { irrigationZoneEmitterId?: TValidIrrigationSchedulerZoneEmitterId }
  eventPanel: null | { eventId: EVENT_ID }
  pulseEventPanel: null | { pulseEventId: EVENT_ID }
  selectedPropertyId: null | number
  mostRecentApiResponseTimestamps: Record<
    | 'irrigation-scheduler-publish'
    | 'irrigation-scheduler-zones-stop'
    | 'irrigation-scheduler-zones-resume'
    | 'irrigation-scheduler-events-stop'
    | 'irrigation-scheduler-events-publish-timeout',
    null | string
  >
  show3rdPartyPublishedEvents: boolean
  showTimeOnBars: boolean
  showPlanningData: boolean
  hasPendingDeletionEvents: boolean
  currentEmitterStatus: Record<TValidIrrigationSchedulerZoneEmitterId, TIrrigationDeviceStatus>
  irrigationPlanningData: {
    dailyEt: TZoneWithETDataArray[] | null
    loading: boolean
    error: string | null
    hasFetched: boolean
    lastFetchedDateKey: string | null
  } | null
}

export const getInitialState = (): TIrrigationSchedulerStoreState => ({
  batchDelete: { visible: false, events: {} },
  showPublishScheduleConfirmation: false,
  showLauncher: false,
  visible: false,
  dateView: TDateViewEnum.WEEK,
  dateFrom: moment
    .tz(moment.tz.guess())
    .day(IRRIGATION_SCHEDULER_FIRST_DAY_OF_WEEK)
    .startOf('day')
    .toISOString(),
  fertigationSettings: [],
  showPeakHours: false,
  selectedIrrigationZoneEmitterIds: [],
  appliedEventsByIrrigationZoneEmitterId: {},
  externalPublishedEventsByIrrigationZoneEmitterId: {},
  externalPublishedPulseIrrigationEventsByIrrigationZoneEmitterId: {},
  scheduledEvents: {},
  scheduledEventsByIrrigationZoneEmitterId: {},
  scheduledPulseIrrigationEvents: {},
  scheduledPulseEventsByIrrigationZoneEmitterId: {},
  stoppedIrrigationZoneEmitters: {},
  eventCreate: null,
  eventPanel: null,
  pulseEventPanel: null,
  selectedPropertyId: null,
  mostRecentApiResponseTimestamps: {
    'irrigation-scheduler-publish': null,
    'irrigation-scheduler-zones-stop': null,
    'irrigation-scheduler-zones-resume': null,
    'irrigation-scheduler-events-stop': null,
    'irrigation-scheduler-events-publish-timeout': null,
  },
  show3rdPartyPublishedEvents: false,
  showTimeOnBars: false,
  showPlanningData: false,
  hasPendingDeletionEvents: false,
  currentEmitterStatus: {},
  irrigationPlanningData: null,
})

export const irrigationSchedulerStore = persistentInit<TIrrigationSchedulerStoreState>({
  initialState: getInitialState(),
  keysToPutInPersistentStorage: {
    batchDelete: false,
    showPublishScheduleConfirmation: false,
    showLauncher: false,
    visible: false,
    dateView: false,
    dateFrom: false,
    fertigationSettings: false,
    showPeakHours: false,
    selectedIrrigationZoneEmitterIds: false,
    appliedEventsByIrrigationZoneEmitterId: false,
    externalPublishedEventsByIrrigationZoneEmitterId: false,
    externalPublishedPulseIrrigationEventsByIrrigationZoneEmitterId: false,
    scheduledEvents: false,
    scheduledEventsByIrrigationZoneEmitterId: false,
    scheduledPulseIrrigationEvents: false,
    scheduledPulseEventsByIrrigationZoneEmitterId: false,
    stoppedIrrigationZoneEmitters: false,
    eventCreate: false,
    eventPanel: false,
    pulseEventPanel: false,
    selectedPropertyId: false,
    mostRecentApiResponseTimestamps: false,
    show3rdPartyPublishedEvents: true,
    showTimeOnBars: true,
    showPlanningData: true,
    hasPendingDeletionEvents: false,
    currentEmitterStatus: false,
    irrigationPlanningData: false,
  },
  keysToPutInURL: {
    batchDelete: false,
    showPublishScheduleConfirmation: false,
    showLauncher: false,
    visible: false,
    dateView: false,
    dateFrom: false,
    fertigationSettings: false,
    showPeakHours: false,
    selectedIrrigationZoneEmitterIds: false,
    appliedEventsByIrrigationZoneEmitterId: false,
    externalPublishedEventsByIrrigationZoneEmitterId: false,
    externalPublishedPulseIrrigationEventsByIrrigationZoneEmitterId: false,
    scheduledEvents: false,
    scheduledEventsByIrrigationZoneEmitterId: false,
    scheduledPulseIrrigationEvents: false,
    scheduledPulseEventsByIrrigationZoneEmitterId: false,
    stoppedIrrigationZoneEmitters: false,
    eventCreate: false,
    eventPanel: false,
    pulseEventPanel: false,
    selectedPropertyId: false,
    mostRecentApiResponseTimestamps: false,
    show3rdPartyPublishedEvents: false,
    showTimeOnBars: false,
    showPlanningData: false,
    hasPendingDeletionEvents: false,
    currentEmitterStatus: false,
    irrigationPlanningData: false,
  },
  storeName: PersistentStoreName.IRRIGATION_SCHEDULER_STORE,
})
